import React, { useState } from 'react';
import { TaskEstimate } from '../../hooks/useTaskEstimates';
import './PendingEstimatesPanel.css';

interface PendingEstimatesPanelProps {
  estimates: TaskEstimate[];
  loading: boolean;
  onApprove: (id: number, notes?: string) => Promise<{ success: boolean; message?: string }>;
  onReject: (id: number, reason: string) => Promise<{ success: boolean; message?: string }>;
  onRefresh: () => void;
  managerId: string;
  managerName: string;
}

const PendingEstimatesPanel: React.FC<PendingEstimatesPanelProps> = ({
  estimates,
  loading,
  onApprove,
  onReject,
  onRefresh,
  managerId,
  managerName
}) => {
  const [actioningId, setActioningId] = useState<number | null>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleApprove = async (estimate: TaskEstimate) => {
    setActioningId(estimate.id);
    setError(null);

    try {
      const result = await onApprove(estimate.id);
      if (!result.success) {
        setError(result.message || 'Failed to approve estimate');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setActioningId(null);
    }
  };

  const handleRejectStart = (id: number) => {
    setRejectingId(id);
    setRejectReason('');
    setError(null);
  };

  const handleRejectCancel = () => {
    setRejectingId(null);
    setRejectReason('');
  };

  const handleRejectConfirm = async (estimate: TaskEstimate) => {
    if (!rejectReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    setActioningId(estimate.id);
    setError(null);

    try {
      const result = await onReject(estimate.id, rejectReason.trim());
      if (result.success) {
        setRejectingId(null);
        setRejectReason('');
      } else {
        setError(result.message || 'Failed to reject estimate');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setActioningId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  if (estimates.length === 0 && !loading) {
    return null; // Don't show panel if no pending estimates
  }

  return (
    <div className="pending-estimates-manager-panel">
      <div className="panel-header">
        <div className="header-left">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
          <h3>Estimates Pending Approval</h3>
          <span className="count-badge">{estimates.length}</span>
        </div>
        <button 
          className="refresh-btn" 
          onClick={onRefresh}
          disabled={loading}
          title="Refresh"
        >
          {loading ? (
            <div className="btn-spinner"></div>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 4v6h-6"></path>
              <path d="M1 20v-6h6"></path>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
          )}
        </button>
      </div>

      {error && (
        <div className="panel-error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="estimates-list">
        {estimates.map((estimate) => (
          <div key={estimate.id} className="estimate-card">
            <div className="estimate-main">
              <div className="estimate-employee">
                <span className="employee-avatar">
                  {estimate.employee_name?.charAt(0) || '?'}
                </span>
                <span className="employee-name">{estimate.employee_name || 'Unknown'}</span>
                <span className="estimate-time">{formatDate(estimate.created_at)}</span>
              </div>
              
              <div className="estimate-task">
                {estimate.task_description}
              </div>
              
              <div className="estimate-details">
                <span className="detail-item type-badge">{estimate.task_type}</span>
                {estimate.project_name && (
                  <span className="detail-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                    </svg>
                    {estimate.project_name}
                  </span>
                )}
                {estimate.account_name && !estimate.project_name && (
                  <span className="detail-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                    {estimate.account_name}
                  </span>
                )}
              </div>

              <div className="estimate-hours-display">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <span className="hours-value">{estimate.estimate_hours}h</span>
                <span className="hours-label">estimated</span>
              </div>

              {estimate.estimate_notes && (
                <div className="estimate-notes">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                  {estimate.estimate_notes}
                </div>
              )}
            </div>

            {rejectingId === estimate.id ? (
              <div className="reject-form">
                <textarea
                  placeholder="Why is this estimate being rejected? What should they change?"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  autoFocus
                />
                <div className="reject-actions">
                  <button 
                    className="reject-cancel"
                    onClick={handleRejectCancel}
                    disabled={actioningId === estimate.id}
                  >
                    Cancel
                  </button>
                  <button 
                    className="reject-confirm"
                    onClick={() => handleRejectConfirm(estimate)}
                    disabled={actioningId === estimate.id || !rejectReason.trim()}
                  >
                    {actioningId === estimate.id ? (
                      <div className="btn-spinner"></div>
                    ) : (
                      'Reject with Feedback'
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="estimate-actions">
                <button 
                  className="action-btn reject"
                  onClick={() => handleRejectStart(estimate.id)}
                  disabled={actioningId === estimate.id}
                  title="Reject estimate"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  Reject
                </button>
                <button 
                  className="action-btn approve"
                  onClick={() => handleApprove(estimate)}
                  disabled={actioningId === estimate.id}
                  title="Approve estimate"
                >
                  {actioningId === estimate.id ? (
                    <div className="btn-spinner"></div>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Approve
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PendingEstimatesPanel;

